import { Button, Text } from '@chakra-ui/react';
import assert from 'assert';
import React from 'react';
import { Link } from 'react-router-dom';

import { FundingRequestActionKeyGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { ActionConfig } from './types';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  dollarNumberWithCommas,
  getFundingRequestPayloadWithAssert,
  getFundingRequestComponentPayloadWithAssert,
} from '~/packages/common-frontend/utils';
import { fundingRequestKeyToVerb } from '~/packages/funding-requests/constants';

// Fragments
type FundingRequestLinkProps = { id: string; children?: React.ReactNode };
/** Component to display link to funding request */
const FundingRequestLink = ({ id, children }: FundingRequestLinkProps) => {
  return (
    <Button
      as={Link}
      aria-label="funding request detail"
      variant="link"
      to={`/funding-requests/${id}`}
    >
      {children || 'funding request'}
    </Button>
  );
};

// Exports
export const fundingRequests: Record<
  FundingRequestActionKeyGQLType,
  ActionConfig
> = {
  requestPending: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const { actorName } = event;
      const payload = getFundingRequestPayloadWithAssert(event);
      const {
        fundingRequest: { id, creditFacilityName },
      } = payload;
      return (
        <Text>
          <strong>{`${actorName} initiated`}</strong>
          {` a `}
          <FundingRequestLink id={id} />
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="play" />,
  },
  requestUnsubmitted: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const payload = getFundingRequestPayloadWithAssert(event);
      const {
        fundingRequest: { id, creditFacilityName },
      } = payload;
      return (
        <Text>
          <FundingRequestLink id={id}>Funding request</FundingRequestLink>
          {` was `}
          <strong>unsubmitted</strong>
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="xmark-large" />,
  },
  requestSubmitted: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const { actorName } = event;
      const payload = getFundingRequestPayloadWithAssert(event);
      const {
        fundingRequest: { id, creditFacilityName },
      } = payload;
      assert(
        creditFacilityName,
        'Credit facility name exists for funding request',
      );
      return (
        <Text>
          <strong>{`${actorName} submitted`}</strong>
          {` a `}
          <FundingRequestLink id={id} />
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="paper-plane" />,
  },
  requestCanceled: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const { actorName } = event;
      const payload = getFundingRequestPayloadWithAssert(event);
      const {
        fundingRequest: { id, creditFacilityName },
      } = payload;
      return (
        <Text>
          <strong>{`${actorName} canceled`}</strong>
          {` a `}
          <FundingRequestLink id={id} />
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="xmark-large" />,
  },
  requestApproved: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const { actorName } = event;
      const payload = getFundingRequestPayloadWithAssert(event);
      const {
        fundingRequest: { id, creditFacilityName },
      } = payload;
      return (
        <Text>
          <strong>{`${actorName} approved`}</strong>
          {` a `}
          <FundingRequestLink id={id} />
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="check" />,
  },
  requestRejected: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const { actorName } = event;
      const payload = getFundingRequestPayloadWithAssert(event);
      const {
        fundingRequest: { id, creditFacilityName },
      } = payload;
      return (
        <Text>
          <strong>{`${actorName} rejected`}</strong>
          {` a `}
          <FundingRequestLink id={id} />
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="xmark-large" />,
  },
  requestExpired: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const payload = getFundingRequestPayloadWithAssert(event);
      const {
        fundingRequest: { id, creditFacilityName },
      } = payload;
      return (
        <Text>
          <FundingRequestLink id={id}>Funding request</FundingRequestLink>
          <strong>expired</strong>
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="xmark-large" />,
  },
  componentSettled: {
    Component: function InsertDeliverableItemComponent({ event }) {
      const payload = getFundingRequestComponentPayloadWithAssert(event);
      const { id, creditFacilityName } = payload.fundingRequest;
      const { amount, type } = payload.fundingRequestComponent;
      return (
        <Text>
          {`A `}
          <strong>{`${dollarNumberWithCommas(amount)} `}</strong>
          <FundingRequestLink
            id={id}
          >{`${fundingRequestKeyToVerb[type]} transfer`}</FundingRequestLink>
          {` was `}
          <strong>completed</strong>
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: <Icon icon="dollar-sign" />,
  },
};
